import { forwardRef, useId, type SelectHTMLAttributes } from 'react';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'children'> {
  /** Visible label text (also wires up the accessible name). */
  label: string;
  options: SelectOption[];
  /** Inline error message shown below the field. */
  error?: string;
  /** Hides the label visually; it stays for assistive tech. */
  hideLabel?: boolean;
}

/**
 * Labelled single-choice control, native on purpose.
 *
 * A native select is a bottom sheet on a phone, is reachable by its label, and
 * needs no script: everything a menu would have to reinvent. It is styled with the
 * same tokens as `Input`, and like it, 16px below `sm` so iOS never zooms.
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, options, id, error, hideLabel = false, className = '', ...rest },
  ref,
) {
  const autoId = useId();
  const selectId = id ?? autoId;
  const errorId = `${selectId}-error`;

  return (
    <div>
      <label
        htmlFor={selectId}
        className={hideLabel ? 'sr-only' : 'mb-2 block text-sm font-medium text-[var(--color-text)]'}
      >
        {label}
      </label>
      <select
        id={selectId}
        ref={ref}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        className={[
          'block w-full min-h-[40px] cursor-pointer rounded-lg border bg-[var(--color-surface)] py-2 pl-3 pr-9 text-base text-[var(--color-text)] focus:outline-hidden focus:ring-2 focus:ring-[var(--color-accent)]/30 sm:text-sm',
          error
            ? 'border-[var(--color-danger)] focus:border-[var(--color-danger)]'
            : 'border-[var(--color-border-control)] focus:border-[var(--color-accent)]',
          className,
        ]
          .filter(Boolean)
          .join(' ')}
        {...rest}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value} disabled={option.disabled}>
            {option.label}
          </option>
        ))}
      </select>
      {error && (
        <p id={errorId} className="mt-1.5 text-xs text-[var(--color-danger)]">
          {error}
        </p>
      )}
    </div>
  );
});
